import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import type useWebSocket from "react-use-websocket";

export type SendJsonMessage = ReturnType<typeof useWebSocket>["sendJsonMessage"];

/** Rewrites a consumer-initiated message before it is sent on a socket. Must be pure. */
export type OutboundMessageTransform = (message: Parameters<SendJsonMessage>[0]) => Parameters<SendJsonMessage>[0];

export interface GaiaSubscription {
    sessionId: string;
    callSid?: string;
    chatId?: string;
    partner: Partner;
    sendJsonMessage: SendJsonMessage;
    logger: Logger;
    timeout?: number;
    explicitSubscriptions?: string[];
}

export interface GaiaUnsubscription {
    sessionId: string;
    callSid?: string;
    chatId?: string;
    sendJsonMessage: SendJsonMessage;
    logger: Logger;
}

export type SubscriptionStatus = "subscribed" | "subscribing" | "unsubscribed" | "unsubscribing";

export interface GaiaWebsocketContext {
    sendJsonMessage: SendJsonMessage;
    loading: boolean;
    /**
     * True when the live connection is the Simplr websocket rather than legacy GAIA — i.e. the URL
     * actually resolved to Simplr (flag on, URL configured, partner eligible), not merely that the
     * `isSimplrWebsocketLiveEnabled` flag is on. Consumers that must only use Simplr-only behaviour
     * (e.g. the `expert-assist-reactive-guidance` appName) gate on this, not on the raw flag.
     * Optional: providers that do not participate in the Simplr cutover (standalone expert-assist,
     * test harnesses) leave it unset, which reads as GAIA.
     */
    isSimplrLive?: boolean;
    subscribeSessionToGaia: ({
        sessionId,
        callSid,
        chatId,
        partner,
        sendJsonMessage,
        timeout,
        explicitSubscriptions,
    }: GaiaSubscription) => Promise<void>;
    unsubscribeSessionFromGaia: ({ sessionId, callSid, chatId, sendJsonMessage }: GaiaUnsubscription) => void;
}

export const GaiaWebSocketEvent = {
    // authorize
    Authorize: "authorize",
    AuthorizationSuccess: "authorization-success",
    UnauthorizedConnection: "unauthorized-connection",
    // subscribe/unsubscribe
    Subscribe: "subscribe",
    Unsubscribe: "unsubscribe",
    SessionSubscriptionSuccess: "session-subscription-success",
    SessionUnsubscriptionSuccess: "session-unsubscription-success",
    // reactive messages
    OrchestratorInvoke: "orchestrator-invoke",
    OrchestratorInvokeSuccess: "orchestrator-invoke-success",
    OrchestratorInvokeError: "orchestrator-invoke-error",
    // proactive messages
    MessageSuggestion: "message-suggestion",
    // session history
    FetchHistory: "fetch-orchestrator-history",
    FetchHistorySuccess: "fetch-orchestrator-history-success",
    FetchHistoryFailure: "fetch-orchestrator-history-error",
    // auto complete
    AutoComplete: "fetch-auto-complete-suggestion",
    AutoCompleteSuccess: "fetch-auto-complete-suggestion-success",
    AutoCompleteFailure: "fetch-auto-complete-suggestion-error",
    // outage information
    OutageInfo: "fetch-helix-news",
    OutageInfoSuccess: "fetch-helix-news-success",
    // transcript snippets
    TranscriptSnippets: "call-transcript",
    // update session context
    UpdateSessionContext: "update-session-context",
    UpdateSessionContextSuccess: "update-session-context-success",
    UpdateSessionContextFailure: "update-session-context-error",
} as const;

export type GaiaWebSocketEventType = (typeof GaiaWebSocketEvent)[keyof typeof GaiaWebSocketEvent];

export interface GaiaWebSocketResponse {
    body: unknown;
    data: any;
    correlationId: string;
    name: GaiaWebSocketEventType;
    timestamp: number;
}

export interface GaiaSubscriptionBody {
    sessionGroupId?: string;
    sessionId: string;
    suggestion?: {
        message: string;
        messageId: string;
    };
}
